import type { RefundWaiver, ScoringSettings } from '@plt/refund-quote'
import { callApi } from '../api'

/** The settings the Refunds screen scores with, and the screen that changes them (refund-quote SPEC.md §10.6). */

export interface ScoringVersion {
  version: number
  settings: ScoringSettings
  reason: string
  publishedBy: string
  publishedAt: string
  restoredFrom: number | null
}

export type WaiverStatus = 'pending' | 'active' | 'rejected' | 'retired'

export interface StoredWaiver extends RefundWaiver {
  id: number
  status: WaiverStatus
  reason: string
  createdBy: string
  createdAt: string
  decidedBy: string | null
  decidedAt: string | null
  retiredBy: string | null
  retiredAt: string | null
}

export interface RefundScoringState {
  live: ScoringVersion | null
  defaults: ScoringSettings
  sourceDescriptions: Record<string, string>
  warningNotes: Record<string, string>
  history: ScoringVersion[]
  waivers: StoredWaiver[]
  you: string | null
  canPublish: boolean
  canApprove: boolean
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

export const getRefundScoring = () =>
  callApi<RefundScoringState>('/bookings/refund-scoring', {}, 'Could not load the refund scoring settings')

export const getLiveScoring = () =>
  callApi<{ version: number | null; settings: ScoringSettings }>(
    '/bookings/refund-scoring/live',
    {},
    'Could not load the live scoring settings',
  )

export const publishScoring = (settings: ScoringSettings, reason: string) =>
  callApi<ScoringVersion>('/bookings/refund-scoring', json({ settings, reason }), 'Could not publish the settings')

export const rollbackScoring = (version: number, reason: string) =>
  callApi<ScoringVersion>('/bookings/refund-scoring/rollback', json({ version, reason }), 'Could not roll back')

export const registerWaiver = (waiver: RefundWaiver, reason: string) =>
  callApi<StoredWaiver>('/bookings/refund-waivers', json({ ...waiver, reason }), 'Could not register the waiver')

export const decideWaiver = (id: number, decision: 'approve' | 'reject') =>
  callApi<StoredWaiver>(`/bookings/refund-waivers/${id}/decision`, json({ decision }), 'Could not record the decision')

export const retireWaiver = (id: number) =>
  callApi<StoredWaiver>(`/bookings/refund-waivers/${id}/retire`, json({}), 'Could not retire the waiver')

export const lookupWaiver = (code: string) =>
  callApi<StoredWaiver>(
    `/bookings/refund-waivers/lookup?code=${encodeURIComponent(code)}`,
    {},
    `No live waiver has the code ${code}`,
  )
